import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { config } from "../lib/config";
import { useApi } from "../lib/useApi";
import type { MeResponse } from "../lib/api";
import { TopbarTabsProvider, useTopbarTabs } from "./TopbarTabs";
import { usePermissions } from "../lib/roles";

type NavItem = { to: string; label: string; desc: string };
type NavGroup = { section: string; items: NavItem[] };

function useNav(): NavGroup[] {
  const perms = usePermissions();
  const groups: NavGroup[] = [];

  if (perms.canManageAdmins) {
    groups.push({
      section: "Tools",
      items: [
        { to: "/", label: "Deploy demo project", desc: "Copy the sample project into a workspace" },
        { to: "/sample-project-master", label: "Sample project master", desc: "Release a verbatim copy of the sample project" },
        { to: "/admins", label: "Admin users", desc: "Manage portal-role memberships" },
      ],
    });
    groups.push({
      section: "Support",
      items: [
        { to: "/support/users", label: "Users", desc: "Look up users and run support actions" },
        { to: "/support/organizations", label: "Organizations", desc: "Browse organizations, licences and members" },
        { to: "/support/create-organization", label: "Create organization", desc: "Sales-assisted signup: create an org for a customer" },
        { to: "/support/deleted-documents", label: "Restore documents", desc: "Find and restore accidentally-deleted documents / drawings" },
      ],
    });
  }

  if (perms.canSeeOutages) {
    groups.push({
      section: "Operations",
      items: [
        { to: "/outages", label: "Scheduled Outages", desc: "Book, review and switch on maintenance windows" },
      ],
    });
  }

  if (perms.canSeeServices) {
    groups.push({
      section: "Observability",
      items: [
        { to: "/observability/services", label: "Services", desc: "Workloads, ingresses, certificates" },
      ],
    });
  }

  if (perms.canSeeCost) {
    groups.push({
      section: "Cost",
      items: [
        { to: "/cost/allocation", label: "Allocation", desc: "Real-billed $ per namespace / workload" },
      ],
    });
  }

  return groups;
}

function TopbarTabs() {
  const tabs = useTopbarTabs();
  if (tabs.length === 0) return null;
  return (
    <nav className="tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className={"tab" + (t.active ? " active" : "")}
          onClick={t.onSelect}
          disabled={!t.onSelect}
        >
          <span className="glyph" />
          {t.label}
        </button>
      ))}
    </nav>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const api = useApi();
  const sub = (auth.user?.profile?.sub as string) ?? "";

  const [me, setMe] = useState<MeResponse | null>(null);
  const nav = useNav();
  useEffect(() => {
    let cancelled = false;
    void api.getMe()
      .then((u) => { if (!cancelled) setMe(u); })
      .catch(() => { /* fall back to sub */ });
    return () => { cancelled = true; };
  }, [api]);

  const display = me?.email || me?.displayName || sub || "—";

  return (
    <TopbarTabsProvider>
      <div className="app">
        <header className="topbar">
          <div className="brand">
            <span className="mark" />
            <span className="wordmark">
              Drawboard<span className="accent">Projects</span>
              <small>ADMIN CONSOLE</small>
            </span>
          </div>
          <TopbarTabs />
          <div className="spacer" />
          <div className="session">
            <div className="who" title={sub || undefined}>
              <b>{display}</b>
              <span>authenticated · {config.adminScope}</span>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => void auth.signoutRedirect()}>
              Sign out
            </button>
          </div>
        </header>

        <aside className="rail">
          {nav.map((group) => (
            <div key={group.section}>
              <div className="rail-label">{group.section}</div>
              {group.items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => "tool" + (isActive ? " active" : "")}>
                  <span className="t">{n.label}</span>
                  <span className="d">{n.desc}</span>
                </NavLink>
              ))}
            </div>
          ))}
          <div className="rail-foot">
            ENV &nbsp;{config.environmentName}
            <br />
            SSO &nbsp;{config.authority.replace(/^https?:\/\//, "")}
            <br />
            API &nbsp;{config.apiBaseUrl.replace(/^https?:\/\//, "")}
            <br />
            ADM &nbsp;{config.adminApiBaseUrl.replace(/^https?:\/\//, "")}
          </div>
        </aside>

        <main className="canvas">
          <span className="regmark tl" />
          <span className="regmark tr" />
          <span className="regmark bl" />
          <span className="regmark br" />
          <div className="canvas-inner">{children}</div>
        </main>

        <footer className="statusbar">
          <div className="cell">
            <span className="led" />
            <span className="k">AUTH</span>
            <span className="v ok">OK</span>
          </div>
          <div className="cell">
            <span className="k">ENV</span>
            <span className="v">{config.environmentName}</span>
          </div>
          <div className="spacer cell" />
          <div className="cell right">DRAWBOARD PROJECTS · INTERNAL ONLY</div>
        </footer>
      </div>
    </TopbarTabsProvider>
  );
}
